<template>
  <b-card
    class="board-card cursor-pointer"
    @click="$emit('click', board)"
  >
    <div class="board-card__top">
      <div
        v-b-tooltip.hover="board.name"
        class="board-card__title"
        :title="board.name"
      >
        <feather-icon
          icon="GridIcon"
          size="16"
          class="text-primary mr-50"
        />
        <span class="board-card__name">{{ board.name }}</span>
      </div>
      <b-dropdown
        v-if="canEditBoards"
        variant="link"
        toggle-class="p-0"
        no-caret
        right
        @click.stop
      >
        <template #button-content>
          <feather-icon
            icon="MoreVerticalIcon"
            size="16"
            class="text-muted"
          />
        </template>
        <b-dropdown-item @click.stop="$emit('edit', board)">
          <feather-icon
            icon="EditIcon"
            size="14"
            class="mr-50"
          />
          {{ $t('Edit') }}
        </b-dropdown-item>
        <b-dropdown-item
          variant="danger"
          @click.stop="$emit('delete', board)"
        >
          <feather-icon
            icon="TrashIcon"
            size="14"
            class="mr-50"
          />
          {{ $t('Delete') }}
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <div class="board-card__meta">
      <span>{{ board.tasks?.total || 0 }} {{ $t(`${board.tasks?.total > 1 ? 'Tasks' : 'Task'}`) }}</span>
      <span
        class="dot"
        aria-hidden="true"
      >•</span>
      <span>{{ board.statuses?.length || 0 }} {{ $t(`${board.statuses?.length > 1 ? 'Columns' : 'Column'}`) }}</span>
      <span
        class="dot"
        aria-hidden="true"
      >•</span>
      <span>{{ board.milestones?.total || 0 }} {{ $t(`${board.milestones?.total > 1 ? 'Milestones' : 'Milestone'}`) }}</span>
    </div>

    <div class="board-card__footer">
      <div class="board-card__avatars avatar-group">

        <b-avatar
          v-for="(user, userIndex) in board.members.data.slice(0, 3)"
          :key="user.uuid"
          :src="getAvatar(user.avatar)"
          :text="getInitials(user.name)"
          size="32"
          :variant="getAvatarVariant(userIndex)"
          class="avatar-item"
        />
        <b-avatar
          v-if="board.members.total > 3"
          size="32"
          variant="secondary"
          class="avatar-item avatar-more"
        >
          <span class="small">+{{ board.members.total - 3 }}</span>
        </b-avatar>
      </div>
      <small>
        {{ $t('Updated on') }} {{ formatDate(board.updatedAt || board.createdAt) }}
      </small>
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BDropdown,
  BDropdownItem,
  BAvatar,
  VBTooltip,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'

export default {
  name: 'BoardCard',
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BCard,
    BDropdown,
    BDropdownItem,
    BAvatar,
  },
  props: {
    board: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters('project', ['USER_ROLE']),
    ...mapGetters('permissions', ['canEditBoards']),
  },
  methods: {
    getInitials(name) {
      if (!name) return '?'
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    },
    getAvatarVariant(index) {
      const variants = ['primary', 'success', 'info', 'warning', 'danger', 'secondary']
      return variants[index % variants.length]
    },
    formatDate(date) {
      if (!date) return ''
      const formatter = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' })
      return formatter.format(new Date(date))
    },
  },
}
</script>

<style lang="scss" scoped>
.board-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  padding: 0.5rem 0.75rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
  }

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.35rem;
  }

  &__title {
    display: flex;
    align-items: center;
    font-weight: 600;
    flex: 1;
    min-width: 0;
    max-width: calc(100% - 30px);

    .feather-icon {
      flex-shrink: 0;
      width: 16px;
    }
  }

  &__name {
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  }

  &__meta {
    display: flex;
    align-items: center;
    font-size: 0.9rem;
    margin-bottom: 0.75rem;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__avatars {
    display: flex;
    align-items: center;
  }

  .more-count {
    background: #f1f5f9;
    color: #0f172a;
    font-weight: 600;
  }

  .avatar-group {
    display: flex;

    .avatar-item {
      margin-left: -8px;
      border: 2px solid #fff;

      &:first-child {
        margin-left: 0;
      }
    }

    .avatar-more {
      font-size: 0.75rem;
      color: #334155;
      background: #e2e8f0;
    }
  }

  .dot {
    margin: 0 0.45rem;
  }
}
</style>
